import { FiCheck, FiLock, FiMapPin } from 'react-icons/fi';
import WarehouseProductDisplay from '@/lib/shared-coms/WarehouseProductDisplay';

/** The SalesOrderVerifyLine fields a row shows. */
export type VerifyGroupRowLine = {
  key: string;
  productName: string;
  ndc?: string | null;
  upc?: string | null;
  deaSchedule?: string | null;
  /** SERIALIZED | BULK | UPC */
  kind: string;
  required: number;
  scanned: number;
  serialNumbers?: readonly string[] | null;
  orphan?: boolean | null;
  /** A controlled product this operator may not verify (scans of it are refused). */
  controlledLocked?: boolean | null;
  pickZone?: { id?: string | null; name?: string | null } | null;
  /** Every zone Pick confirmed for the product (a line picked zone by zone has several). */
  pickZones?: readonly ({ id?: string | null; name?: string | null } | null)[] | null;
};

/** "A-01, B-04": every zone Pick took the product from, else the one `pickZone`. */
export const verifyPickZonesLabel = (line: VerifyGroupRowLine): string | null => {
  const names = (line.pickZones?.length ? line.pickZones : [line.pickZone])
    .map((zone) => zone?.name?.trim())
    .filter((name): name is string => !!name);
  return names.length ? [...new Set(names)].join(', ') : null;
};

type VerifyGroupRowProps = {
  line: VerifyGroupRowLine;
  /** Alternates on every accepted scan of this row, so the flash animation restarts each time. */
  flash?: 'a' | 'b' | null;
  /**
   * Some of `line.scanned` is still saving: the page counts an accepted scan straight away and the
   * server's answer confirms it (or a refusal takes it back off).
   */
  saving?: boolean;
};

/**
 * One product group on the Verify list ("lists all Sales Order products by NDC/UPC"): the product
 * as every floor screen shows it, what Pick confirmed it from, and `scanned / required` — green
 * when exactly complete, red when over (or when the group is not on the order at all: units of a
 * product no line asks for are listed only so they can be removed). The count includes scans that
 * are still saving, dimmed until the server has them. A controlled product the operator may not
 * verify is listed and counted like the rest, flagged as locked.
 */
function VerifyGroupRow({ line, flash = null, saving = false }: VerifyGroupRowProps) {
  const over = line.scanned > line.required;
  const pickZonesLabel = verifyPickZonesLabel(line);
  const done =
    !line.orphan && !over && line.required > 0 && line.scanned === line.required;
  const product = {
    title: line.productName,
    // The row is built from the group's own fields rather than a nested Product: an orphan group
    // (units of a product no line names) may have no vendor Product to select.
    fdaProduct:
      line.ndc || line.deaSchedule
        ? { elevenDigitNdc: line.ndc, deaSchedule: line.deaSchedule }
        : null,
    upcProduct: line.upc ? { upc: line.upc } : null
  };
  const serials = line.serialNumbers || [];
  const lastSerial = serials.length ? serials[serials.length - 1] : null;

  return (
    <div
      className={[
        'verify-group',
        done && 'verify-group--done',
        (over || line.orphan) && 'verify-group--over',
        line.controlledLocked && 'verify-group--locked',
        flash && `verify-group--flash-${flash}`,
        saving && 'verify-group--saving'
      ]
        .filter(Boolean)
        .join(' ')}
      data-key={line.key}
    >
      <div className="verify-group__product">
        <WarehouseProductDisplay product={product}>
          {line.controlledLocked && (
            <span className="verify-group__flag verify-group__flag--locked">
              <FiLock aria-hidden /> {line.deaSchedule ? `${line.deaSchedule} · ` : ''}
              Controlled — needs a controlled verifier
            </span>
          )}
          {line.orphan && (
            <span className="verify-group__flag">Not on this order — remove</span>
          )}
          {!line.orphan && over && (
            <span className="verify-group__flag">
              Over by {line.scanned - line.required} — remove
            </span>
          )}
          {!!pickZonesLabel && (
            <span className="verify-group__zone">
              <FiMapPin aria-hidden /> Picked from {pickZonesLabel}
            </span>
          )}
          {line.kind === 'SERIALIZED' && !!lastSerial && (
            <span className="verify-group__serial">Last serial {lastSerial}</span>
          )}
        </WarehouseProductDisplay>
      </div>
      <div
        className="verify-group__count"
        aria-label={`${line.scanned} of ${line.required}${saving ? ', saving' : ''}`}
        aria-busy={saving || undefined}
      >
        {done && <FiCheck aria-hidden />}
        <span>
          {line.scanned}/{line.required}
        </span>
      </div>
    </div>
  );
}

export default VerifyGroupRow;
